import type { NotificationTone } from '@viquard/doc-ui';
import type { i18n as I18nInstance } from 'i18next';
import { formatRelativeTime } from '@/lib/datetime';

const TRANSLATION_NAMESPACES = new Set([
  'admin',
  'articles',
  'auth',
  'bookmarks',
  'calendar',
  'common',
  'dashboard',
  'editor',
  'files',
  'groups',
  'settings',
]);

const PAYLOAD_COPY_KEYS: Readonly<Record<string, string>> = {
  'A comment was approved.': 'notifications.payloadCopy.commentApprovedBody',
  'A forum thread has a new reply.': 'notifications.payloadCopy.forumReplyFallbackBody',
  'A moderator updated the status of your comment.': 'notifications.payloadCopy.commentModerationBody',
  'A new comment was posted.': 'notifications.payloadCopy.newCommentFallbackBody',
  'A newsletter issue was delivered.': 'notifications.payloadCopy.newsletterDeliveredBody',
  'A selected time was added to the group calendar.': 'notifications.payloadCopy.coordinationSlotCommittedBody',
  'A survey reached its response cap and was closed.': 'notifications.payloadCopy.surveyCapBody',
  'A survey received a new response.': 'notifications.payloadCopy.surveyResponseBody',
  'A committed availability coordination slot was cancelled.': 'notifications.payloadCopy.coordinationSlotCancelledBody',
  'A committed availability coordination slot was updated.': 'notifications.payloadCopy.coordinationScheduleChangedBody',
  'An article you annotated was edited and the highlighted passage no longer matches. Open the article to re-anchor or remove the annotation.': 'notifications.payloadCopy.annotationOrphanedBody',
  'Availability response needed': 'notifications.payloadCopy.availabilityResponseNeededTitle',
  'Availability result saved': 'notifications.payloadCopy.availabilityResultSavedTitle',
  'Choose the times you can attend.': 'notifications.payloadCopy.chooseTimesBody',
  'Child password reset completed': 'notifications.payloadCopy.childPasswordResetCompletedTitle',
  'Child password reset request': 'notifications.payloadCopy.childPasswordResetRequestTitle',
  'Group invitation': 'notifications.payloadCopy.groupInvitationTitle',
  'New availability poll': 'notifications.payloadCopy.newAvailabilityPollTitle',
  'New comment': 'notifications.payloadCopy.newCommentTitle',
  'New comment on your article': 'notifications.payloadCopy.newCommentOnArticleTitle',
  'New forum reply': 'notifications.payloadCopy.newForumReplyTitle',
  'New reaction on your article': 'notifications.payloadCopy.newReactionTitle',
  'New survey response': 'notifications.payloadCopy.newSurveyResponseTitle',
  'Newsletter sent': 'notifications.payloadCopy.newsletterSentTitle',
  'Newsletter test sent': 'notifications.payloadCopy.newsletterTestSentTitle',
  'No matching office-hour time': 'notifications.payloadCopy.noMatchingOfficeHourTitle',
  'Open the article to read the new comment.': 'notifications.payloadCopy.openArticleCommentBody',
  'Open the article to see who is saving your work.': 'notifications.payloadCopy.openArticleBookmarkBody',
  'Open the forum thread to read the reply.': 'notifications.payloadCopy.openForumReplyBody',
  'Open the forum thread to view the accepted answer.': 'notifications.payloadCopy.openForumAcceptedBody',
  'Open the invitation to accept it.': 'notifications.payloadCopy.openInvitationBody',
  'Office-hour time cancelled': 'notifications.payloadCopy.officeHourCancelledTitle',
  'Office-hour time changed': 'notifications.payloadCopy.officeHourChangedTitle',
  'Please choose the times you can attend.': 'notifications.payloadCopy.pleaseChooseTimesBody',
  'Someone bookmarked your article': 'notifications.payloadCopy.bookmarkedArticleTitle',
  'Someone reacted to your article. Open it to see the new count.': 'notifications.payloadCopy.reactionBody',
  'Someone replied to your comment': 'notifications.payloadCopy.repliedToCommentTitle',
  'Someone replied to your forum post': 'notifications.payloadCopy.repliedToForumPostTitle',
  'Survey response cap reached': 'notifications.payloadCopy.surveyCapTitle',
  'The committed times do not match your availability.': 'notifications.payloadCopy.coordinationUnavailableBody',
  'You have been invited to join a group.': 'notifications.payloadCopy.groupInvitationBody',
  'You were mentioned in a comment': 'notifications.payloadCopy.mentionedInCommentTitle',
  'You were mentioned in a forum thread': 'notifications.payloadCopy.mentionedInForumTitle',
  'Your annotation lost its anchor': 'notifications.payloadCopy.annotationOrphanedTitle',
  'Your answer was accepted': 'notifications.payloadCopy.answerAcceptedTitle',
  'Your comment was hidden': 'notifications.payloadCopy.commentHiddenTitle',
  'Your comment was marked as spam': 'notifications.payloadCopy.commentSpamTitle',
};

export function notificationCopy(raw: string, i18n: I18nInstance): string {
  const [maybeNamespace, ...keyParts] = raw.split('.');
  if (
    maybeNamespace !== undefined &&
    keyParts.length > 0 &&
    TRANSLATION_NAMESPACES.has(maybeNamespace)
  ) {
    const key = keyParts.join('.');
    if (i18n.exists(key, { ns: maybeNamespace })) {
      return i18n.t(key, { ns: maybeNamespace });
    }
  }
  const key = PAYLOAD_COPY_KEYS[raw];
  if (key !== undefined) return i18n.t(key);
  if (raw.startsWith('Invitation to ')) {
    return i18n.t('notifications.payloadCopy.invitationTo', {
      groupName: raw.slice('Invitation to '.length),
    });
  }
  const requestedFor = /^Password reset requested for (.+)\.$/.exec(raw);
  if (requestedFor !== null) {
    return i18n.t('notifications.payloadCopy.childPasswordResetRequestedFor', {
      name: requestedFor[1],
    });
  }
  const completedFor = /^Password reset for (.+) was completed\.$/.exec(raw);
  if (completedFor !== null) {
    return i18n.t('notifications.payloadCopy.childPasswordResetCompletedFor', {
      name: completedFor[1],
    });
  }
  if (i18n.exists(raw)) return i18n.t(raw);
  return raw;
}

export function formatNotificationTime(value: string, locale: string): string {
  const formatted = formatRelativeTime(value, locale);
  return formatted === value ? '' : formatted;
}

export function notificationTone(kind: string): NotificationTone {
  if (kind.startsWith('comment.')) return 'mention';
  if (kind.startsWith('bookmark.')) return 'success';
  if (kind === 'article.published') return 'success';
  if (kind.startsWith('article.reaction')) return 'success';
  if (kind.startsWith('annotation.')) return 'warning';
  if (kind.startsWith('user.password-reset') || kind.startsWith('user.child-password-reset')) {
    return 'danger';
  }
  return 'info';
}

export function humanizeNotificationKind(kind: string): string {
  return kind
    .split(/[.-]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

export type NotificationActionTarget =
  | { kind: 'same-origin'; href: string }
  | { kind: 'external'; href: string };

export function resolveNotificationAction(
  actionUrl: string,
  origin: string,
): NotificationActionTarget {
  const url = new URL(actionUrl, origin);
  if (url.origin === origin) {
    return { kind: 'same-origin', href: `${url.pathname}${url.search}${url.hash}` };
  }
  return { kind: 'external', href: url.toString() };
}

export function openNotificationAction(actionUrl: string): void {
  if (typeof window === 'undefined') return;
  const target = resolveNotificationAction(actionUrl, window.location.origin);
  if (target.kind === 'same-origin') {
    window.location.assign(target.href);
    return;
  }
  window.open(target.href, '_blank', 'noopener,noreferrer');
}
